"use client"

import { useState, useEffect } from "react"
import { TextField, Button, IconButton, Paper, Typography, Box, Grid } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
const BASE_URL = process.env.REACT_APP_BASE_URL
const token = sessionStorage.getItem("token")

const SettingsV2 = () => {
  const [email, setEmail] = useState("")
  const [numbers, setNumbers] = useState([""])
  const [allowedIps, setAllowedIps] = useState([])
  const [file, setFile] = useState(null)
  const [fileBase64, setFileBase64] = useState("")
  const [whatsappKey, setWhatsappKey] = useState("")
  const [lifetimesmsApiToken, setLifetimesmsApiToken] = useState("")
  const [lifetimesmsApiSecret, setLifetimesmsApiSecret] = useState("")
  const [numberErrors, setNumberErrors] = useState([])
  const [ipErrors, setIpErrors] = useState([])
  const [fileError, setFileError] = useState("")

  const acceptedTypes = ["image/png", "image/jpeg", "application/pdf"]

  useEffect(() => {
    loadSettings() // Call GET API on mount
  }, [])

  const loadSettings = async () => {
    try {
      const response = await fetch(`${BASE_URL}/applicant/get_settings`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      const data = await response.json()

      setEmail(data.email || "")
      setWhatsappKey(data.whatsapp_api_key || "")
      setLifetimesmsApiToken(data.lifetimesms_api_token || "")
      setLifetimesmsApiSecret(data.lifetimesms_api_secret || "")
      const phones = data.phones.replace(/'/g, '"') || []
      setNumbers(data.phones ? JSON.parse(phones) : [""])
      if (!data.allowed_ips) {
        setAllowedIps([])
      } else {
        const ips = data.allowed_ips.replace(/'/g, '"')
        const parsed = JSON.parse(ips)
        const validIps = Array.isArray(parsed) ? parsed.filter((ip) => ip && ip.trim()) : []
        setAllowedIps(validIps)
      }
      setFileBase64(data.ad_url || "")
    } catch (error) {
      console.error("Error fetching settings:", error)
      toast.error("Failed to load settings.")
    }
  }

  const validatePhoneNumber = (number) => {
    const pattern = /^\+92-\d{3}-\d{7}$/
    return pattern.test(number)
  }

  const validateIp = (ip) => {
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/
    const ipv6Pattern = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/
    return ipv4Pattern.test(ip) || ipv6Pattern.test(ip)
  }

  const handleNumberChange = (index, value) => {
    const updatedNumbers = [...numbers]
    updatedNumbers[index] = value
    setNumbers(updatedNumbers)

    const errors = [...numberErrors]
    errors[index] = !validatePhoneNumber(value)
    setNumberErrors(errors)
  }

  const handleIpChange = (index, value) => {
    const updatedIps = [...allowedIps]
    updatedIps[index] = value
    setAllowedIps(updatedIps)

    const errors = [...ipErrors]
    errors[index] = value.trim() ? !validateIp(value) : false
    setIpErrors(errors)
  }

  const handleAddNumber = () => {
    setNumbers([...numbers, ""])
    setNumberErrors([...numberErrors, false])
  }

  const handleAddIp = () => {
    setAllowedIps([...allowedIps, ""])
    setIpErrors([...ipErrors, false])
  }

  const getValidAllowedIps = () => allowedIps.map((ip) => ip.trim()).filter(Boolean)

  const handleRemoveNumber = (index) => {
    const updatedNumbers = numbers.filter((_, i) => i !== index)
    const updatedErrors = numberErrors.filter((_, i) => i !== index)
    setNumbers(updatedNumbers)
    setNumberErrors(updatedErrors)
  }

  const handleRemoveIp = (index) => {
    const updatedIps = allowedIps.filter((_, i) => i !== index)
    const updatedErrors = ipErrors.filter((_, i) => i !== index)
    setAllowedIps(updatedIps)
    setIpErrors(updatedErrors)
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    setFileError("")

    if (selectedFile && !acceptedTypes.includes(selectedFile.type)) {
      setFile(null)
      setFileBase64("")
      setFileError("Only PNG, JPG, and PDF files are allowed.")
      return
    }

    setFile(selectedFile)

    if (selectedFile) {
      const reader = new FileReader()
      reader.readAsDataURL(selectedFile)
      reader.onload = () => {
        setFileBase64(reader.result)
      }
      reader.onerror = (error) => {
        console.error("Error converting file to base64:", error)
      }
    }
  }

  const openFileInNewTab = () => {
    if (!fileBase64) return
    // if fileBase64  is not a valid base64 string, return
    if (fileBase64.startsWith("data:")) {
      const base64Parts = fileBase64.split(",")
      const mime = base64Parts[0].match(/:(.*?);/)[1]
      const byteCharacters = atob(base64Parts[1])
      const byteArrays = []

      for (let i = 0; i < byteCharacters.length; i++) {
        byteArrays.push(byteCharacters.charCodeAt(i))
      }

      const byteArray = new Uint8Array(byteArrays)
      const blob = new Blob([byteArray], { type: mime })
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, "_blank")
    } else {
      window.open(fileBase64, "_blank")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate all phone numbers before submission
    const errors = numbers.map((num) => !validatePhoneNumber(num))
    setNumberErrors(errors)
    if (errors.includes(true)) {
      alert("Please fix phone number format errors before submitting.")
      return
    }

    const ipValidationErrors = allowedIps.map((ip) => (ip.trim() ? !validateIp(ip) : false))
    setIpErrors(ipValidationErrors)
    if (ipValidationErrors.includes(true)) {
      alert("Please fix IP address format errors before submitting.")
      return
    }

    if (file && !fileBase64) {
      alert("Invalid file selected.")
      return
    }

    const validAllowedIps = getValidAllowedIps()

    const formData = {
      email,
      whatsappKey,
      lifetimesmsApiToken,
      lifetimesmsApiSecret,
      phoneNumbers: numbers,
      allowedIps: validAllowedIps.length ? validAllowedIps : null,
      fileBase64,
    }

    try {
      const response = await fetch(`${BASE_URL}/applicant/update_settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      const result = await response.json()
      console.log("Success:", result)
      toast.success("Settings saved successfully!")
    } catch (error) {
      console.error("Error submitting form:", error)
      alert("Failed to save settings. Please try again.")
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <Paper elevation={3} className="p-4 p-md-5 mx-auto mt-4" style={{ maxWidth: "600px" }}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Email */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Support Email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>

            {/* WhatsApp Key */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="WhatsApp API Key"
                value={whatsappKey}
                onChange={(e) => setWhatsappKey(e.target.value)}
              />
            </Grid>

            {/* Lifetime SMS API Token */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Lifetime SMS API Token"
                value={lifetimesmsApiToken}
                onChange={(e) => setLifetimesmsApiToken(e.target.value)}
              />
            </Grid>

            {/* Lifetime SMS API Secret */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Lifetime SMS API Secret"
                value={lifetimesmsApiSecret}
                onChange={(e) => setLifetimesmsApiSecret(e.target.value)}
                type="text"
              />
            </Grid>

            {/* Phone Numbers */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Support Phone Numbers
              </Typography>
              {numbers.map((number, index) => (
                <Box key={index} className="mb-3" display="flex" flexDirection="column">
                  <TextField
                    label={`Support Phone Number ${index + 1}`}
                    value={number}
                    onChange={(e) => handleNumberChange(index, e.target.value)}
                    fullWidth
                    required
                    error={numberErrors[index]}
                    helperText={numberErrors[index] ? "Format must be +92-XXX-XXXXXXX" : ""}
                  />
                  {numbers.length > 1 && (
                    <Box alignSelf="flex-end" mt={1}>
                      <IconButton color="error" onClick={() => handleRemoveNumber(index)} aria-label="remove">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}

              <Button onClick={handleAddNumber} variant="text" color="primary">
                + Add another number
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Allowed IP Addresses
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Optional. Leave empty to allow staff from any IP address.
              </Typography>
              {allowedIps.length === 0 && (
                <Typography variant="body2" color="text.secondary" className="mb-2">
                  No IP restrictions configured.
                </Typography>
              )}
              {allowedIps.map((ip, index) => (
                <Box key={index} className="mb-3" display="flex" flexDirection="column">
                  <TextField
                    label={`Allowed IP Address ${index + 1}`}
                    value={ip}
                    onChange={(e) => handleIpChange(index, e.target.value)}
                    fullWidth
                    error={ipErrors[index]}
                    helperText={ipErrors[index] ? "Format must be valid IPv4 (e.g., 192.168.1.1) or IPv6" : ""}
                  />
                  <Box alignSelf="flex-end" mt={1}>
                    <IconButton color="error" onClick={() => handleRemoveIp(index)} aria-label="remove">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <Button onClick={handleAddIp} variant="text" color="primary">
                {allowedIps.length === 0 ? "+ Add IP restriction" : "+ Add another IP"}
              </Button>
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Upload Advertisement File (PNG, JPG, PDF)
              </Typography>
              <Button variant="contained" component="label" style={{ color: "white" }}>
                Upload File
                <input hidden type="file" accept=".png,.jpg,.jpeg,.pdf" onChange={handleFileChange} />
              </Button>
              {file && <span className="ms-2">{file.name}</span>}
              {fileError && (
                <Typography variant="body2" color="error" mt={1}>
                  {fileError}
                </Typography>
              )}

              {/* View File */}
              {fileBase64 && (
                <Box mt={2}>
                  <Button variant="contained" color="primary" style={{ color: "white" }} onClick={openFileInNewTab}>
                    View File
                  </Button>
                </Box>
              )}
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" style={{ color: "white" }} fullWidth>
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </DashboardLayout>
  )
}

export default SettingsV2
